// src/pages/shelbula/workspaces/ConfirmationModals/ConfirmRollbackToPlanModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileClock, AlertTriangle, X } from 'lucide-react';

const ConfirmRollbackToPlanModal = ({ isOpen, onClose, onConfirm, task }) => {
    const taskName = task?.objective || 'the selected phase';
    const objectiveRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isClamped, setIsClamped] = useState(false);

    useEffect(() => {
        if (isOpen && objectiveRef.current) {
            setIsClamped(objectiveRef.current.scrollHeight > objectiveRef.current.clientHeight);
        } else {
            setIsClamped(false);
            setIsExpanded(false);
        }
    }, [taskName, isOpen]);

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="relative w-full max-w-md bg-surface border border-border-color rounded-xl shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start gap-4 p-4">
                            <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-surface-dark">
                                <FileClock size={20} className="text-info" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-on-surface">Rollback to Plan?</h3>
                                <p className="text-sm text-on-surface-dim mt-2">
                                    Rolling back to the planning stage for the following phase:
                                </p>
                                <div className="mt-2 bg-background-dark border border-border-color rounded-md p-3">
                                    <p ref={objectiveRef} className={`text-sm text-on-surface font-medium ${!isExpanded ? 'line-clamp-3' : ''}`}>
                                        {taskName}
                                    </p>
                                    {(isClamped || isExpanded) && (
                                        <button onClick={() => setIsExpanded(!isExpanded)} className="text-primary text-xs font-semibold mt-2 hover:underline">
                                            {isExpanded ? 'Show less' : 'Show more'}
                                        </button>
                                    )}
                                </div>
                                <div className="text-sm text-warning p-3 rounded-md mt-4 flex items-start gap-2.5 bg-surface-dark border border-border-color">
                                    <AlertTriangle size={24} className="flex-shrink-0" />
                                    <span>Any work performed during this phase (and any work after it) will be permanently lost.</span>
                                </div>
                            </div>
                             <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full text-on-surface-dim hover:bg-surface-dark transition-colors">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="flex justify-end gap-3 p-4 bg-background-dark border-t border-border-color">
                            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-md bg-surface-dark hover:bg-surface-raised text-on-surface transition-colors">
                                Nevermind
                            </button>
                            <button
                                onClick={onConfirm}
                                className="px-4 py-2 text-sm font-semibold rounded-md bg-info text-white hover:brightness-110 transition-colors flex items-center gap-2"
                            >
                                <FileClock size={14} />
                                Yes, Rollback to Plan
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default ConfirmRollbackToPlanModal;